import { ProjectStatus, projectStatus, ReportingEnabled, reportingEnabled } from '@/modules/Projects/Projects.constants';
import { FieldSummaryType } from '@/modules/Report/Report.schema';
import { clsCat, cn } from '@/styles/styles.utils';
import { cva } from 'class-variance-authority';
import { FC } from 'react';

// Utility function to determine badge size based on character length
export function getBadgeSize(characterLength: number) {
  if (characterLength < 2) return 'xs';
  if (characterLength < 4) return 's';
  if (characterLength < 6) return 'm';
  if (characterLength < 7) return 'l';
  return 'xl';
}

// Function and types to get max badge sizes per column. Used on field summary table
type ValueColorCell = {
  value: string | null;
  color: 'red' | 'yellow' | 'green' | null;
};

type BadgeFieldKeys = {
  [K in keyof FieldSummaryType]: FieldSummaryType[K] extends ValueColorCell ? K : never;
}[keyof FieldSummaryType]; // -> union of keys like 'pH' | 'caMpl' | etc.

export function getMaxBadgeSizes(data: FieldSummaryType[], columns: BadgeFieldKeys[]): Record<BadgeFieldKeys, BadgeProps['size']> {
  const maxLengths: Partial<Record<BadgeFieldKeys, number>> = {};

  for (const col of columns) {
    maxLengths[col] = data.reduce((max, item) => {
      const value = item[col]?.value;
      const length = value != null ? value.length : 0;
      return Math.max(max, length);
    }, 0);
  }

  const badgeSizes = columns.reduce(
    (acc, col) => {
      const len = maxLengths[col] || 0;
      acc[col] = getBadgeSize(len);
      return acc;
    },
    {} as Record<BadgeFieldKeys, BadgeProps['size']>
  );

  return badgeSizes;
}

// Badge component
const badgeVariants = cva(
  clsCat({
    base: 'w-[4rem] px-0 py-1 px-2',
    border: 'border rounded-full',
  }),
  {
    variants: {
      color: {
        red: 'bg-error-200/20 border border-error-200',
        yellow: 'bg-yellow/20 border border-yellow',
        green: 'bg-green/20 border border-green',
      },
      size: {
        xs: 'w-[2rem]', // 1 character
        s: 'w-[3rem]', // 2-3 character
        m: 'w-[5rem]', // 4-5 characters
        l: 'w-[6rem]', // 6 characters
        xl: 'w-[7rem]', // 7 - 8 characters
      },
      textAlign: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      },
    },
    defaultVariants: {
      color: 'red',
      size: 'm',
      textAlign: 'right',
    },
  }
);

export type BadgeProps = {
  value: number | string | null;
  color?: 'red' | 'yellow' | 'green' | null;
  size?: 'xs' | 's' | 'm' | 'l' | 'xl';
  textAlign?: 'left' | 'center' | 'right';
  className?: string;
};

export const Badge: React.FC<BadgeProps> = ({ value, color = null, className, size = 'm', textAlign = 'right' }) => {
  return <div className={cn(badgeVariants({ color, size, textAlign }), className)}>{value}</div>;
};

// StatusBadge component
const statusLabelMap = {
  [projectStatus.INCOMPLETE]: 'Incomplete',
  [projectStatus.PARTIAL]: 'Partial',
  [projectStatus.COMPLETE]: 'Complete',
  [reportingEnabled.NOT_AVAILABLE]: 'Not Available',
} as const;

const StatusBadgeVariants = cva(
  clsCat({
    base: 'w-[6rem] px-0 py-2 text-center',
    border: 'rounded-full',
    text: 'text-neutral-0 text-xs text-center',
  }),
  {
    variants: {
      color: {
        grey: 'bg-neutral-400',
        orange: 'bg-orange',
        green: 'bg-green',
      },
    },
    defaultVariants: {
      color: 'grey',
    },
  }
);

type StatusBadgeProps = {
  status: ProjectStatus | ReportingEnabled;
  className?: string;
};

function getStatusBadgeColor(status: string) {
  if (status === projectStatus.INCOMPLETE || status === 'none' || status === 'no' || status === 'Not Available') return 'grey';
  if (status === projectStatus.PARTIAL) return 'orange';
  return 'green';
}

export const StatusBadge: FC<StatusBadgeProps> = ({ status, className }) => {
  const color = getStatusBadgeColor(status);
  const badgeText = statusLabelMap[status] || status;
  return <div className={cn(StatusBadgeVariants({ color }), className)}>{badgeText}</div>;
};
